import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, Tabs, Tab, CircularProgress, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const typeColors = (t) => {
  if (!t) return { bg: '#eceff1', color: '#455a64' };
  if (t.includes('overdue') || t.includes('declined') || t.includes('rejected')) return { bg: '#ffebee', color: '#c62828' };
  if (t.includes('paid') || t.includes('signed') || t.includes('accepted') || t.includes('approved')) return { bg: '#e8f5e9', color: '#2e7d32' };
  if (t.includes('contract')) return { bg: '#ede7f6', color: '#5e35b1' };
  if (t.includes('proposal')) return { bg: '#fff8e1', color: '#e65100' };
  if (t.includes('invoice')) return { bg: '#e3f2fd', color: '#1565c0' };
  if (t.includes('scope')) return { bg: '#f3e5f5', color: '#7b1fa2' };
  return { bg: '#eceff1', color: '#455a64' };
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/notifications');
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) { setItems([]); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleClick = async (item) => {
    if (!item.read_at) {
      try {
        await api.put(`/notifications/${item.id}/read`);
        setItems(items.map(i => i.id === item.id ? { ...i, read_at: new Date().toISOString() } : i));
      } catch (e) {}
    }
    if (item.link_path) navigate(item.link_path);
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      const now = new Date().toISOString();
      setItems(items.map(i => ({ ...i, read_at: i.read_at || now })));
    } catch (e) {}
  };

  const filtered = tab === 'unread' ? items.filter(i => !i.read_at) : items;
  const unreadCount = items.filter(i => !i.read_at).length;

  return (
    <Layout>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', fontSize: 32 }}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </Typography>
        </Box>
        {unreadCount > 0 && (
          <Button onClick={handleMarkAllRead} variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#1a237e', color: '#1a237e' }}>
            Mark all as read
          </Button>
        )}
      </Box>

      <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #e8eaf6', px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          <Tab value="all" label={`All (${items.length})`} />
          <Tab value="unread" label={`Unread (${unreadCount})`} />
        </Tabs>

        {loading ? (
          <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <NotificationsNoneIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
            <Typography sx={{ color: '#999' }}>
              {tab === 'unread' ? 'No unread notifications.' : 'No notifications yet.'}
            </Typography>
          </Box>
        ) : (
          <Box>
            {filtered.map(item => {
              const c = typeColors(item.type);
              const isUnread = !item.read_at;
              return (
                <Box key={item.id} onClick={() => handleClick(item)}
                  sx={{ px: 3, py: 2, cursor: 'pointer', display: 'flex', gap: 2, alignItems: 'flex-start',
                    borderBottom: '1px solid #f5f6fa',
                    bgcolor: isUnread ? '#E6F1FB' : 'white',
                    '&:hover': { bgcolor: isUnread ? '#d3e4f7' : '#f5f6fa' } }}>
                  <Box sx={{ bgcolor: c.bg, color: c.color, borderRadius: 1, px: 1, py: 0.25, fontSize: 11, fontWeight: 700, flexShrink: 0, mt: 0.5 }}>
                    {item.type ? item.type.split('_')[0] : 'note'}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: isUnread ? 700 : 500, fontSize: 14, color: '#1a237e' }}>
                      {item.title}
                    </Typography>
                    {item.body && (
                      <Typography sx={{ fontSize: 13, color: '#555', mt: 0.5 }}>{item.body}</Typography>
                    )}
                    <Typography sx={{ fontSize: 11, color: '#999', mt: 0.5 }}>{timeAgo(item.created_at)}</Typography>
                  </Box>
                  {isUnread && <FiberManualRecordIcon sx={{ color: '#1976d2', fontSize: 10, mt: 1 }} />}
                </Box>
              );
            })}
          </Box>
        )}
      </Card>
    </Layout>
  );
}
